import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  Download,
  Eye,
  FileChartColumn,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  PanelLeft,
  RefreshCw,
  Scan,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import useStore, { PLAN_SLOTS, visiblePlanKeys } from '@/stores/useStore'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import ReportStudioSettings from '@/components/report/ReportStudioSettings'
import { REPORT_SECTIONS, REPORT_SECTION_GROUPS, isSectionEnabled } from '@/lib/reportConfig'
import { HTML_REPORT_SECTIONS } from '@/lib/htmlExport/interactive'
import { planDisplayName } from '@/lib/planDisplayNames'
import { cn, downloadFile } from '@/lib/utils'

const REPORT_TYPES = [
  {
    id: 'one-page',
    label: 'One-page reports',
    description: 'Client-ready snapshots for a meeting or follow-up.',
  },
  {
    id: 'full',
    label: 'Detailed reports',
    description: 'Plan review, comparison, employee detail, and charts.',
  },
  {
    id: 'data',
    label: 'Data export',
    description: 'Calculated employee results for further analysis.',
  },
]

// Each report family keeps its own badge colour so the three cards stay
// distinguishable at a glance instead of reading as one grey grid.
const REPORT_TYPE_BADGES = {
  'one-page': { icon: FileSpreadsheet, wrap: 'bg-primary/10', tint: 'text-primary' },
  full: { icon: FileText, wrap: 'bg-amber-100/70', tint: 'text-amber-700' },
  data: { icon: Download, wrap: 'bg-emerald-100/70', tint: 'text-emerald-700' },
}

function ReportFamilyPreview({ type }) {
  if (type === 'full') {
    return (
      <div className="relative h-12 overflow-hidden border border-border bg-muted/20" aria-hidden="true">
        <div className="absolute inset-y-2 left-[18%] right-[8%] translate-x-2 border border-border bg-background/60" />
        <div className="absolute inset-y-1.5 left-[14%] right-[12%] translate-x-1 border border-border bg-background/80" />
        <div className="absolute inset-y-1 left-[10%] right-[16%] border border-border bg-background px-2 py-1.5">
          <div className="h-1 w-12 bg-foreground/35" />
          <div className="mt-1.5 grid grid-cols-[1.3fr_.7fr] gap-1.5">
            <div className="space-y-1">
              <div className="h-1 bg-muted-foreground/20" />
              <div className="h-1 bg-muted-foreground/20" />
              <div className="h-1 bg-muted-foreground/20" />
            </div>
            <div className="flex items-end gap-0.5 border-b border-border px-1">
              <div className="h-2 flex-1 bg-foreground/20" />
              <div className="h-4 flex-1 bg-foreground/35" />
              <div className="h-3 flex-1 bg-foreground/25" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (type === 'data') {
    return (
      <div className="h-12 overflow-hidden border border-border bg-background p-1.5" aria-hidden="true">
        <div className="grid h-full grid-cols-4 border-l border-t border-border">
          {Array.from({ length: 16 }, (_, index) => (
            <div key={index} className={cn('border-b border-r border-border', index < 4 ? 'bg-muted' : 'bg-background')} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="h-12 overflow-hidden border border-border bg-muted/20 p-1.5" aria-hidden="true">
      <div className="mx-auto h-full max-w-52 border border-border bg-background px-2 py-1.5">
        <div className="flex items-center justify-between">
          <div className="h-1 w-12 bg-foreground/35" />
          <div className="h-1 w-5 bg-muted-foreground/20" />
        </div>
        <div className="mt-1.5 grid grid-cols-3 gap-1">
          <div className="h-3 bg-muted" />
          <div className="h-3 bg-muted" />
          <div className="h-3 bg-muted" />
        </div>
        <div className="mt-1.5 h-1 bg-muted-foreground/15" />
      </div>
    </div>
  )
}

const EMPTY_CHECK_SUMMARY = {
  issueCount: 0,
  flagCount: 0,
  needsDataCount: 0,
  status: 'clear',
  label: 'No modeled check flags',
}

export default function ReportStudio({
  hasInputErrors,
  isAuthenticated,
  isGenerating,
  onPreviewReport,
  onDownloadReport,
  onExportSpreadsheet,
  onExportCsv,
  onArtifactDownload,
  onReviewChecks,
  checkSummary = EMPTY_CHECK_SUMMARY,
  comparing = false,
  detailedHtmlDownloadFormat = 'html',
  htmlIsFullDetail = false,
}) {
  const reportConfig = useStore((state) => state.reportConfig)
  const lastCalculation = useStore((state) => state.lastCalculation)
  const visibleKeys = useStore((state) => visiblePlanKeys(state).join('|')).split('|').filter(Boolean)
  const planNames = useStore((state) => PLAN_SLOTS.map((key) => state[key]?.name || '').join('|')).split('|')
  const shownPlanNames = PLAN_SLOTS.flatMap((key, index) => (
    visibleKeys.includes(key) ? [planDisplayName(key, planNames[index])] : []
  ))
  const baselineName = shownPlanNames[0] || 'Current Plan'
  const comparedPlanNames = shownPlanNames.length > 1
    ? shownPlanNames.join(', ')
    : `${baselineName} and every shown option`
  const [activeFormatId, setActiveFormatId] = useState('detailed-pdf')
  const [previewArtifact, setPreviewArtifact] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [previewUpdatedAt, setPreviewUpdatedAt] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [zoom, setZoom] = useState(90)
  const [fitWidth, setFitWidth] = useState(true)
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false)
  const [checkDialogOpen, setCheckDialogOpen] = useState(false)
  const [reportPickerOpen, setReportPickerOpen] = useState(true)
  const requestRef = useRef(0)
  const previewInFlightRef = useRef(false)
  const queuedPreviewRef = useRef(null)
  const previewHandlerRef = useRef(onPreviewReport)
  previewHandlerRef.current = onPreviewReport

  const allFormats = useMemo(() => [
    {
      id: 'detailed-pdf',
      label: 'PDF',
      reportType: 'full',
      previewLabel: comparing ? 'Detailed comparison PDF' : `${baselineName} detailed PDF`,
      description: comparing
        ? `Detailed pages use ${baselineName}; the comparison section includes ${comparedPlanNames}.`
        : `Complete, print-ready report for ${baselineName}.`,
      icon: FileText,
      previewFormat: 'detailed-pdf',
      download: () => onDownloadReport('pdf'),
      comparison: true,
    },
    {
      id: 'detailed-html',
      label: 'Interactive HTML',
      reportType: 'full',
      previewLabel: comparing ? 'Interactive comparison report' : `${baselineName} interactive report`,
      description: comparing
        ? `Uses ${baselineName} as the baseline and includes every shown option in shared comparison sections.`
        : `Standalone report for ${baselineName} with interactive charts and employee detail.`,
      icon: FileChartColumn,
      previewFormat: 'detailed-html',
      download: () => onDownloadReport(detailedHtmlDownloadFormat),
      comparison: true,
    },
    {
      id: 'prospect-pdf',
      label: 'PDF',
      reportType: 'one-page',
      previewLabel: `${baselineName} one-page summary`,
      description: `Concise landscape summary for ${baselineName}.`,
      icon: FileText,
      previewFormat: 'pdf',
      download: () => onDownloadReport('prospect'),
    },
    {
      id: 'prospect-html',
      label: 'Interactive HTML',
      reportType: 'one-page',
      previewLabel: `${baselineName} interactive one-page summary`,
      description: `Compact interactive summary for ${baselineName}.`,
      icon: FileChartColumn,
      previewFormat: 'html',
      download: () => onDownloadReport('prospect-html'),
    },
    {
      id: 'spreadsheet',
      label: 'Excel workbook',
      reportType: 'one-page',
      previewLabel: `${baselineName} one-page Excel workbook`,
      description: `${baselineName} proposal totals, allocation classes, and analysis charts.`,
      icon: FileSpreadsheet,
      previewFormat: 'spreadsheet',
      download: onExportSpreadsheet,
    },
    {
      id: 'csv',
      label: 'Results CSV',
      reportType: 'data',
      previewLabel: 'Results CSV',
      description: `${baselineName} employee-level calculated results for analysis and records.`,
      icon: FileSpreadsheet,
      previewFormat: 'csv',
      download: onExportCsv,
    },
  ], [baselineName, comparedPlanNames, comparing, detailedHtmlDownloadFormat, onDownloadReport, onExportCsv, onExportSpreadsheet])

  const formats = allFormats

  const activeFormat = formats.find((format) => format.id === activeFormatId) || formats[0]
  const reportTypes = REPORT_TYPES
  const settingsFormat = activeFormat.id === 'detailed-html' && htmlIsFullDetail ? 'full-detail-html' : activeFormat.id
  const missingClient = ['detailed-pdf', 'detailed-html', 'prospect-pdf', 'prospect-html'].includes(activeFormat.id)
    && !reportConfig.clientName?.trim()
  const registry = activeFormat.id === 'detailed-html' ? HTML_REPORT_SECTIONS : REPORT_SECTIONS
  const sectionRoots = activeFormat.id === 'detailed-html'
    ? HTML_REPORT_SECTIONS.filter((section) => !section.parent).map((section) => section.key)
    : REPORT_SECTION_GROUPS
  const noSections = ['detailed-pdf', 'detailed-html'].includes(activeFormat.id)
    && !(activeFormat.id === 'detailed-html' && htmlIsFullDetail)
    && sectionRoots.every((key) => !isSectionEnabled(reportConfig, key, registry))
  const showSettings = !['spreadsheet', 'csv'].includes(activeFormat.id)
  const openFormat = (formatId) => {
    setActiveFormatId(formatId)
    setReportPickerOpen(false)
  }
  // Sequential so the browser does not drop concurrent saves, and so a failure
  // stops the rest instead of half-succeeding quietly.
  const downloadAllInGroup = async (groupFormats) => {
    for (const format of groupFormats) await format.download()
  }

  const previewBlocked = !isAuthenticated || hasInputErrors || missingClient || noSections
  const exportDisabled = previewBlocked || isGenerating || previewLoading || Boolean(previewError && !previewArtifact)
  const previewKey = JSON.stringify({ format: activeFormat.previewFormat, reportConfig, lastCalculation, refreshKey })

  useEffect(() => {
    if (!formats.some((format) => format.id === activeFormatId)) setActiveFormatId(formats[0]?.id || 'detailed-pdf')
  }, [activeFormatId, formats])

  useEffect(() => {
    setPreviewArtifact(null)
    setPreviewError('')
    setPreviewUpdatedAt(null)
    setFitWidth(true)
  }, [activeFormat.id])

  const drainPreviewQueue = useCallback(async () => {
    if (previewInFlightRef.current) return
    const request = queuedPreviewRef.current
    if (!request) return

    queuedPreviewRef.current = null
    previewInFlightRef.current = true
    setPreviewLoading(true)
    setPreviewError('')
    try {
      const artifact = await previewHandlerRef.current(request.format)
      if (requestRef.current === request.id) {
        if (artifact) {
          setPreviewArtifact(artifact)
          setPreviewUpdatedAt(Date.now())
        } else {
          setPreviewError('The preview could not be generated. Review the report settings and try again.')
        }
      }
    } catch (error) {
      if (requestRef.current === request.id) {
        setPreviewError(error?.message || 'The preview could not be generated. Review the report settings and try again.')
      }
    } finally {
      previewInFlightRef.current = false
      if (queuedPreviewRef.current) void drainPreviewQueue()
      else setPreviewLoading(false)
    }
  }, [])

  useEffect(() => {
    const requestId = requestRef.current + 1
    requestRef.current = requestId
    queuedPreviewRef.current = null
    if (previewBlocked) {
      setPreviewLoading(false)
      return undefined
    }
    const timer = window.setTimeout(async () => {
      queuedPreviewRef.current = { id: requestId, format: activeFormat.previewFormat }
      await drainPreviewQueue()
    }, 550)
    return () => window.clearTimeout(timer)
  }, [activeFormat.previewFormat, drainPreviewQueue, previewBlocked, previewKey])

  const downloadActive = async () => {
    if (previewArtifact?.content !== undefined && previewArtifact?.fileName) {
      downloadFile(previewArtifact.content, previewArtifact.fileName, previewArtifact.mimeType)
      onArtifactDownload?.(activeFormat.id)
      return
    }
    await activeFormat.download()
  }

  const requestExport = () => {
    if (checkSummary.issueCount > 0) setCheckDialogOpen(true)
    else void downloadActive()
  }

  const blockerMessage = !isAuthenticated
    ? 'Sign in to preview and export'
    : hasInputErrors
      ? 'Fix plan inputs to continue'
      : missingClient
        ? 'Add a client or plan sponsor name'
        : noSections
          ? 'Include at least one report chapter'
          : null

  const previewStatus = blockerMessage
    || (previewLoading ? 'Updating preview…'
      : previewError ? 'Preview needs attention'
        : previewArtifact && previewUpdatedAt ? 'Updated just now'
          : 'Preparing preview…')

  const renderPreview = () => {
    if (previewBlocked) {
      return (
        <div className="flex h-full flex-col items-center justify-center px-8 text-center">
          <AlertTriangle className="h-7 w-7 text-amber-700" aria-hidden="true" />
          <p className="mt-3 text-sm font-semibold">{blockerMessage}</p>
          <p className="mt-1 max-w-sm text-xs leading-5 text-muted-foreground">
            {!isAuthenticated
              ? 'You can configure the report now. Sign in when you are ready to preview or export.'
              : 'The preview refreshes automatically after you resolve this item.'}
          </p>
        </div>
      )
    }

    if (!previewArtifact) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
          {previewError || 'Building the report from the current results…'}
        </div>
      )
    }

    if (previewArtifact.kind === 'pdf') {
      return (
        <div
          role="region"
          aria-label={`${activeFormat.previewLabel} pages`}
          tabIndex={0}
          className="h-full overflow-auto overscroll-contain bg-stone-200/70 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50 sm:p-6"
        >
          <div
            className={cn('mx-auto grid gap-5 transition-[width] duration-150', fitWidth && 'w-full max-w-[72rem]')}
            style={fitWidth ? undefined : { width: `${zoom}%`, minWidth: '28rem' }}
          >
            {previewArtifact.imageUrls.map((imageUrl, index) => (
              <figure key={imageUrl}>
                <img
                  src={imageUrl}
                  alt={`${activeFormat.previewLabel}, page ${index + 1}`}
                  width={previewArtifact.imageSizes?.[index]?.width || 1200}
                  height={previewArtifact.imageSizes?.[index]?.height || 1553}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  className="w-full bg-white shadow-[0_12px_38px_rgba(28,25,23,0.16)]"
                />
              </figure>
            ))}
          </div>
        </div>
      )
    }

    return (
      <iframe
        title={`${activeFormat.previewLabel} live preview`}
        srcDoc={previewArtifact.html}
        sandbox="allow-scripts"
        className="h-full w-full bg-white"
      />
    )
  }

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[1600px] flex-col py-3">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3 pb-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight text-balance">Reports</h2>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
            <span>{blockerMessage || (previewLoading ? 'Preparing export' : previewArtifact ? 'Ready to export' : 'Preparing preview')}</span>
            {!blockerMessage && checkSummary.issueCount > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <button type="button" onClick={onReviewChecks} className="font-medium text-amber-800 underline decoration-amber-800/30 underline-offset-4 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
                  {checkSummary.issueCount} {checkSummary.issueCount === 1 ? 'issue' : 'issues'} to review
                </button>
              </>
            ) : null}
          </div>
        </div>
        {!reportPickerOpen ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setReportPickerOpen(true)} className="gap-2 whitespace-nowrap">
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              All reports
            </Button>
            <Button type="button" onClick={requestExport} disabled={exportDisabled} className="gap-2 whitespace-nowrap">
              <Download className="h-4 w-4" aria-hidden="true" />
              {isGenerating ? 'Preparing…' : 'Export report'}
            </Button>
          </div>
        ) : null}
      </header>

      {reportPickerOpen ? (
      <nav
        aria-label="Report formats"
        className="min-h-0 flex-1 overflow-auto"
      >
        {/* No heading or status of its own: the pane header above already
            names this page and reports the one blocking condition, so
            repeating either here just doubles the same sentence. */}
        <div className="mx-auto w-full max-w-6xl pb-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {reportTypes.map((type) => {
              const groupedFormats = allFormats.filter((format) => format.reportType === type.id)
              const reportScope = type.id === 'full' && comparing ? 'All shown plans' : `${baselineName} only`
              const badge = REPORT_TYPE_BADGES[type.id]
              const BadgeIcon = badge.icon
              return (
                <section
                  key={type.id}
                  aria-labelledby={`report-type-${type.id}`}
                  className="flex min-w-0 rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex w-full min-w-0 flex-col">
                    <ReportFamilyPreview type={type.id} />
                    <div className="mt-4 flex items-center gap-2">
                      <span className={cn('shrink-0 rounded-md p-1.5', badge.wrap)}>
                        <BadgeIcon className={cn('h-4 w-4', badge.tint)} aria-hidden="true" />
                      </span>
                      <h3 id={`report-type-${type.id}`} className="font-semibold">{type.label}</h3>
                      <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{reportScope}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{type.description}</p>
                    {/* One row per format, wide enough to hold the whole label on
                        a single line: preview on the left, download on the right,
                        the way this page used to read. */}
                    <div role="group" aria-labelledby={`report-type-${type.id}`} className="mt-auto grid gap-2 pt-4">
                      {groupedFormats.map((format) => {
                        const Icon = format.icon
                        const selected = format.id === activeFormat.id
                        return (
                          <div key={format.id} className="flex min-h-10 items-center gap-2 rounded-md border border-border bg-background/80 py-1 pl-3 pr-1.5">
                            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                            <button
                              type="button"
                              aria-label={`${type.label}: ${format.label}`}
                              aria-pressed={selected}
                              onClick={() => openFormat(format.id)}
                              className="min-w-0 flex-1 whitespace-nowrap text-left text-sm font-medium leading-tight hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                            >
                              {format.label}
                            </button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                              aria-label={`Preview ${format.label}`}
                              title={`Preview ${format.label}`}
                              onClick={() => openFormat(format.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                              aria-label={`Download ${format.label}`}
                              title={`Download ${format.label}`}
                              disabled={hasInputErrors || isGenerating}
                              onClick={() => format.download()}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        )
                      })}
                      {type.id === 'one-page' && (
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-1 w-full justify-between border-primary/30 bg-background/80 text-foreground shadow-none hover:border-primary/30 hover:bg-primary/5 hover:text-foreground"
                          disabled={hasInputErrors || isGenerating}
                          onClick={() => downloadAllInGroup(groupedFormats)}
                        >
                          <span>{isGenerating ? 'Preparing reports…' : 'Download all'}</span>
                          <Download className="h-4 w-4 text-primary" />
                        </Button>
                      )}
                    </div>
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      </nav>
      ) : (
      <div className={cn('grid min-h-0 flex-1 overflow-hidden border border-border bg-card', showSettings && 'lg:grid-cols-[18rem_minmax(0,1fr)]')}>
        {showSettings ? (
          <aside className="hidden min-h-0 border-r border-border lg:block" aria-label="Report settings">
            <ReportStudioSettings format={settingsFormat} />
          </aside>
        ) : null}

        <section aria-label={`${activeFormat.previewLabel} live preview`} className="flex min-h-0 min-w-0 flex-col overflow-hidden bg-card">
          <div className="flex min-h-12 shrink-0 flex-wrap items-center gap-2 border-b border-border px-2.5 py-2 sm:px-3">
            {showSettings ? (
              <button type="button" onClick={() => setMobileSettingsOpen(true)} className="inline-flex h-8 items-center gap-1.5 rounded border border-border px-2 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 lg:hidden">
                <PanelLeft className="h-3.5 w-3.5" aria-hidden="true" />Settings
              </button>
            ) : null}

            <div className="hidden min-w-0 flex-1 items-baseline gap-2 sm:flex">
              <span className="shrink-0 text-xs font-semibold">{activeFormat.previewLabel}</span>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-1">
              <span className={cn('mr-1 hidden text-[10px] font-medium sm:inline', (previewError || blockerMessage) ? 'text-amber-800' : 'text-muted-foreground')} aria-live="polite">
                {previewStatus}
              </span>
              {previewArtifact?.kind === 'pdf' ? (
                <>
                  <button type="button" aria-label="Fit report preview to width" aria-pressed={fitWidth} onClick={() => setFitWidth(true)} className={cn('rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50', fitWidth && 'bg-muted text-foreground')}>
                    <Scan className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <button type="button" aria-label="Zoom out report preview" onClick={() => { setFitWidth(false); setZoom((value) => Math.max(50, value - 10)) }} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
                    <ZoomOut className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <span className="hidden w-10 text-center text-[10px] tabular-nums text-muted-foreground sm:inline">{fitWidth ? 'Fit' : `${zoom}%`}</span>
                  <button type="button" aria-label="Zoom in report preview" onClick={() => { setFitWidth(false); setZoom((value) => Math.min(130, value + 10)) }} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
                    <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </>
              ) : null}
              <button type="button" aria-label="Refresh report preview" onClick={() => setRefreshKey((value) => value + 1)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
                <RefreshCw className={cn('h-3.5 w-3.5', previewLoading && 'animate-spin motion-reduce:animate-none')} aria-hidden="true" />
              </button>
            </div>
          </div>

          {previewError && previewArtifact ? (
            <div className="shrink-0 border-b border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" role="status">
              {previewError} The last successful preview remains visible.
            </div>
          ) : null}
          <div className="min-h-0 flex-1">{renderPreview()}</div>
        </section>
      </div>
      )}

      <Dialog open={mobileSettingsOpen} onOpenChange={setMobileSettingsOpen}>
        <DialogContent className="left-0 top-0 h-[100dvh] max-h-[100dvh] w-[min(92vw,24rem)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0 lg:hidden">
          <DialogTitle className="sr-only">Report settings</DialogTitle>
          <DialogDescription className="sr-only">Configure the report cover, chapters, and branding.</DialogDescription>
          <ReportStudioSettings format={settingsFormat} />
        </DialogContent>
      </Dialog>

      <AlertDialog open={checkDialogOpen} onOpenChange={setCheckDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export with check issues?</AlertDialogTitle>
            <AlertDialogDescription>
              {checkSummary.label}. These are modeled indicators, not official compliance testing. Review them before sharing the report or acknowledge them and export now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onReviewChecks}>Review checks</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setCheckDialogOpen(false); void downloadActive() }}>Export anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
